import { escapeHtml } from './_shared/htmlEscape.js'
import { emailWrapper, dataRow, sectionHeader } from './_shared/emailWrapper.js'

/** Company (payee) info line shown on payment confirmations */
const COMPANY_INFO_LINE = 'רוסמן לוגיסטיקה בע״מ, ח.פ: 513854901, חלוצי התעשייה 48 חיפה'

/**
 * Generate Cash Payment Confirmation email HTML
 * Supports split cash/checks payments with approval number
 * @param {Object} data - Payment data
 * @param {string} data.clientName - Client name
 * @param {string} data.clientNum - Client number
 * @param {string} [data.clientVat] - Client VAT
 * @param {number} data.amount - Total payment amount
 * @param {number} [data.cashAmount] - Cash portion (0 if none)
 * @param {number} [data.checksAmount] - Checks portion (0 if none)
 * @param {Array} [data.checksDetails] - Array of { checkNumber, date, amount }
 * @param {string} data.agentName - Agent name
 * @param {string} data.agentNumber - Agent number
 * @param {Array} data.invoices - Array of invoice objects { IVNUM, BALDATE, FNCDATE, amount }
 * @param {boolean} [data.isManual] - Whether this is a manual amount (no invoices)
 * @param {string} [data.paymentApprovalNum] - Payment approval number
 * @param {string} [data.note] - Optional agent note, rendered as "הערת סוכן:"
 * @returns {string} Complete HTML email
 */

/** Test-mail notice (shown when data.isTest is true) */
export const TEST_RECEIPT_NOTICE =
  'זהו מייל ניסיון לבדיקת כתובת המייל. המייל נשלח אוטומטית בדיוק כפי שישלחו קבלות אמיתיות בעת יצירתן'

function testNoticeHtml(data, compact = false) {
  if (!data.isTest) return ''
  return `
    <div style="text-align: center; margin-bottom: ${compact ? '12' : '16'}px; padding: ${compact ? '8' : '12'}px 16px; background-color: #fffbeb; border: 1px solid #f59e0b; border-radius: 8px; color: #92400e; font-size: ${compact ? '11' : '13'}px; font-weight: 600;">
      ${TEST_RECEIPT_NOTICE}
    </div>`
}

export function generateCashPaymentEmailHtml(data) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return String(dateStr)
    return d.toLocaleDateString('he-IL', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
      amount || 0
    )
  }

  const now = new Date()
  const dateStr = now.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const timeStr = now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })

  const cashAmount = data.cashAmount || 0
  const checksAmount = data.checksAmount || 0
  const checksDetails = data.checksDetails || []
  const invoices = data.invoices || []
  const hasCash = cashAmount > 0
  const hasChecks = checksAmount > 0

  // Determine payment method label
  let paymentMethodLabel = 'מזומן'
  if (hasCash && hasChecks) paymentMethodLabel = 'מזומן + שיקים'
  else if (hasChecks) paymentMethodLabel = 'שיקים'

  // Build invoices section
  let invoicesSection = ''
  if (data.isManual || invoices.length === 0) {
    invoicesSection = `
    <div style="text-align: center; padding: 16px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 20px;">
      <span style="color: #64748b; font-size: 14px;">תשלום ידני - לא נבחרו חשבוניות</span>
    </div>`
  } else {
    const invoiceRows = invoices
      .map(
        (inv, i) => `
    <tr>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: center; color: #64748b; font-size: 13px; border-bottom: 1px solid #e2e8f0; width: 40px;">
        ${i + 1}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: right; color: #1e293b; font-size: 13px; font-weight: 500; border-bottom: 1px solid #e2e8f0;">
        ${escapeHtml(String(inv.IVNUM || ''))}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: right; color: #475569; font-size: 13px; border-bottom: 1px solid #e2e8f0;">
        ${formatDate(inv.BALDATE)}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: right; color: #475569; font-size: 13px; border-bottom: 1px solid #e2e8f0;">
        ${formatDate(inv.FNCDATE)}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: left; color: #1e293b; font-size: 13px; font-weight: 600; border-bottom: 1px solid #e2e8f0; direction: ltr; white-space: nowrap;">
        ${formatCurrency(inv.amount)}
      </td>
    </tr>`
      )
      .join('')

    invoicesSection = `
    <div style="font-weight: 600; color: #334155; font-size: 14px; margin-bottom: 8px;">
      פירוט חשבוניות (${invoices.length})
    </div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin-bottom: 20px;">
      <tr>
        <th style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 10px 16px; text-align: center; font-weight: 600; color: #283548; font-size: 12px; border-bottom: 2px solid #283548; width: 40px;">#</th>
        <th style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 10px 16px; text-align: right; font-weight: 600; color: #283548; font-size: 12px; border-bottom: 2px solid #283548;">מס׳ חשבונית</th>
        <th style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 10px 16px; text-align: right; font-weight: 600; color: #283548; font-size: 12px; border-bottom: 2px solid #283548;">תאריך</th>
        <th style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 10px 16px; text-align: right; font-weight: 600; color: #283548; font-size: 12px; border-bottom: 2px solid #283548;">ת. פרעון</th>
        <th style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); padding: 10px 16px; text-align: left; font-weight: 600; color: #283548; font-size: 12px; border-bottom: 2px solid #283548;">סכום</th>
      </tr>
      ${invoiceRows}
      <tr>
        <td colspan="4" style="background-color: #f1f5f9; padding: 12px 16px; text-align: right; font-weight: 700; color: #1e293b; font-size: 14px; border-top: 2px solid #283548;">
          סה״כ
        </td>
        <td style="background-color: #f1f5f9; padding: 12px 16px; text-align: left; font-weight: 700; color: #059669; font-size: 14px; border-top: 2px solid #283548; direction: ltr; white-space: nowrap;">
          ${formatCurrency(data.amount)}
        </td>
      </tr>
    </table>`
  }

  // Build payment breakdown section
  let breakdownRows = ''
  if (hasCash) {
    breakdownRows += dataRow('מזומן', formatCurrency(cashAmount), false)
  }
  if (hasChecks) {
    breakdownRows += dataRow('שיקים', formatCurrency(checksAmount), hasCash)
  }

  // Build checks detail table
  let checksSection = ''
  if (hasChecks && checksDetails.length > 0) {
    const checkRows = checksDetails
      .map(
        (c, i) => `
    <tr>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: center; color: #64748b; font-size: 13px; border-bottom: 1px solid #e2e8f0; width: 40px;">
        ${i + 1}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: right; color: #1e293b; font-size: 13px; font-weight: 500; border-bottom: 1px solid #e2e8f0;">
        ${escapeHtml(String(c.checkNumber || ''))}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: right; color: #475569; font-size: 13px; border-bottom: 1px solid #e2e8f0;">
        ${formatDate(c.date)}
      </td>
      <td style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'}; padding: 10px 16px; text-align: left; color: #1e293b; font-size: 13px; font-weight: 600; border-bottom: 1px solid #e2e8f0; direction: ltr; white-space: nowrap;">
        ${formatCurrency(c.amount)}
      </td>
    </tr>`
      )
      .join('')

    checksSection = `
    <div style="font-weight: 600; color: #334155; font-size: 14px; margin-bottom: 8px; margin-top: 20px;">
      פירוט שיקים (${checksDetails.length})
    </div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin-bottom: 20px;">
      <tr>
        <th style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 10px 16px; text-align: center; font-weight: 600; color: #92400e; font-size: 12px; border-bottom: 2px solid #d97706; width: 40px;">#</th>
        <th style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 10px 16px; text-align: right; font-weight: 600; color: #92400e; font-size: 12px; border-bottom: 2px solid #d97706;">מס׳ שיק</th>
        <th style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 10px 16px; text-align: right; font-weight: 600; color: #92400e; font-size: 12px; border-bottom: 2px solid #d97706;">תאריך פרעון</th>
        <th style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 10px 16px; text-align: left; font-weight: 600; color: #92400e; font-size: 12px; border-bottom: 2px solid #d97706;">סכום</th>
      </tr>
      ${checkRows}
      <tr>
        <td colspan="3" style="background-color: #fffbeb; padding: 12px 16px; text-align: right; font-weight: 700; color: #1e293b; font-size: 14px; border-top: 2px solid #d97706;">
          סה״כ שיקים
        </td>
        <td style="background-color: #fffbeb; padding: 12px 16px; text-align: left; font-weight: 700; color: #d97706; font-size: 14px; border-top: 2px solid #d97706; direction: ltr; white-space: nowrap;">
          ${formatCurrency(checksAmount)}
        </td>
      </tr>
    </table>`
  }

  // Build agent note section
  let noteSection = ''
  if (data.note) {
    noteSection = `
    <div style="margin-bottom: 20px; padding: 12px 16px; background-color: #fffbeb; border: 1px solid #fde68a; border-radius: 12px;">
      <span style="font-weight: 600; color: #92400e; font-size: 13px;">הערת סוכן:</span>
      <span style="color: #78350f; font-size: 13px; white-space: pre-wrap;"> ${escapeHtml(data.note)}</span>
    </div>`
  }

  // Build signature section
  let signatureSection = ''
  if (data.signatureUrl) {
    signatureSection = `
    <div style="text-align: center; margin-top: 24px; padding: 20px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px;">
      <div style="font-weight: 600; color: #334155; font-size: 14px; margin-bottom: 12px;">חתימה</div>
      <div style="display: inline-block; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px;">
        <img src="${escapeHtml(data.signatureUrl)}" alt="חתימה" style="max-width: 280px; max-height: 140px; display: block;" />
      </div>
    </div>`
  }

  const content = `
    <div style="text-align: center; margin-bottom: 14px; color: #64748b; font-size: 11.5px;">
      ${COMPANY_INFO_LINE}
    </div>
    ${testNoticeHtml(data)}
    ${
      data.paymentApprovalNum
        ? `
    <div style="text-align: center; margin-bottom: 16px;">
      <div style="display: inline-block; background-color: #f1f5f9; color: #283548; padding: 6px 20px; border-radius: 8px; font-size: 16px; font-weight: 700; letter-spacing: 1px; border: 1px solid #e2e8f0;">
        אישור מס׳ ${escapeHtml(String(data.paymentApprovalNum))}
      </div>
    </div>`
        : ''
    }

    <h2 style="margin: 0 0 4px; color: #1e293b; font-size: 20px; font-weight: 600; text-align: center;">
      ${escapeHtml(data.clientName || '')}
    </h2>
    <p style="margin: 0 0 4px; color: #64748b; font-size: 14px; text-align: center;">
      מקט לקוח: ${escapeHtml(String(data.clientNum || ''))}
    </p>
    <p style="margin: 0 0 4px; color: #64748b; font-size: 14px; text-align: center;">
      תשלום התקבל בתאריך ${dateStr} בשעה ${timeStr}
    </p>
    <p style="margin: 0 0 24px; color: #64748b; font-size: 14px; text-align: center;">
      סוכן: ${escapeHtml(data.agentName || '')} (${escapeHtml(String(data.agentNumber || ''))})
    </p>

    <!-- Payment Summary -->
    <div style="text-align: center; margin-bottom: 24px; padding: 20px; background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); border-radius: 12px;">
      <div style="color: #065f46; font-size: 14px; font-weight: 500; margin-bottom: 4px;">סכום שהתקבל</div>
      <div style="color: #065f46; font-size: 32px; font-weight: 700; direction: ltr; display: inline-block;">
        ${formatCurrency(data.amount)}
      </div>
    </div>

    <!-- Details Table -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin-bottom: 20px;">
      ${sectionHeader('פרטי התשלום', '💵')}
      ${dataRow('אמצעי תשלום', paymentMethodLabel, false)}
      ${breakdownRows}
      ${dataRow('סכום כולל', formatCurrency(data.amount), true)}
      ${dataRow('תאריך', dateStr, false)}
      ${dataRow('שעה', timeStr, true)}
    </table>

    <!-- Agent Note -->
    ${noteSection}

    <!-- Checks Section -->
    ${checksSection}

    <!-- Invoices Section -->
    ${invoicesSection}

    <!-- Signature Section -->
    ${signatureSection}
  `

  return emailWrapper(content, `אישור תשלום מזומן - ${escapeHtml(data.clientName || '')}`)
}

/**
 * Generate a clean PDF version of the cash payment confirmation
 * Same data shape as generateCashPaymentEmailHtml but clean/professional layout
 * @param {Object} data - Payment data (same as generateCashPaymentEmailHtml)
 * @returns {string} Complete HTML document for PDF rendering
 */
export function generateCashPaymentPdfHtml(data) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return String(dateStr)
    return d.toLocaleDateString('he-IL', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
      amount || 0
    )
  }

  const now = new Date()
  const dateStr = now.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const timeStr = now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })

  const cashAmount = data.cashAmount || 0
  const checksAmount = data.checksAmount || 0
  const checksDetails = data.checksDetails || []
  const invoices = data.invoices || []
  const hasCash = cashAmount > 0
  const hasChecks = checksAmount > 0

  let paymentMethodLabel = 'מזומן'
  if (hasCash && hasChecks) paymentMethodLabel = 'מזומן + שיקים'
  else if (hasChecks) paymentMethodLabel = 'שיקים'

  // Invoices table
  let invoicesSection = ''
  if (data.isManual || invoices.length === 0) {
    invoicesSection = `
    <div style="text-align: center; padding: 12px; border: 1px solid #d1d5db; margin-bottom: 16px;">
      <span style="color: #6b7280; font-size: 13px;">תשלום ידני — לא נבחרו חשבוניות</span>
    </div>`
  } else {
    const invoiceRows = invoices
      .map(
        (inv, i) => `
    <tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f9fafb'};">
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">${i + 1}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 12px; font-weight: 500;">${escapeHtml(String(inv.IVNUM || ''))}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 12px; color: #374151;">${formatDate(inv.BALDATE)}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 12px; color: #374151;">${formatDate(inv.FNCDATE)}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 12px; font-weight: 600; direction: ltr; white-space: nowrap;">${formatCurrency(inv.amount)}</td>
    </tr>`
      )
      .join('')

    invoicesSection = `
    <div class="pdf-section">
      <div style="font-weight: 600; font-size: 13px; margin-bottom: 6px; color: #111827;">חשבוניות (${invoices.length})</div>
      <table width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #d1d5db; margin-bottom: 16px;">
        <tr>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: center; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827; width: 36px;">#</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: right; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">מס׳ חשבונית</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: right; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">תאריך</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: right; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">ת. פרעון</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: left; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">סכום</th>
        </tr>
        ${invoiceRows}
        <tr>
          <td colspan="4" style="background: #f3f4f6; padding: 8px 10px; text-align: right; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af;">סה״כ</td>
          <td style="background: #f3f4f6; padding: 8px 10px; text-align: left; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af; direction: ltr; white-space: nowrap;">${formatCurrency(data.amount)}</td>
        </tr>
      </table>
    </div>`
  }

  // Checks table
  let checksSection = ''
  if (hasChecks && checksDetails.length > 0) {
    const checkRows = checksDetails
      .map(
        (c, i) => `
    <tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f9fafb'};">
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">${i + 1}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 12px; font-weight: 500;">${escapeHtml(String(c.checkNumber || ''))}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-size: 12px; color: #374151;">${formatDate(c.date)}</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 12px; font-weight: 600; direction: ltr; white-space: nowrap;">${formatCurrency(c.amount)}</td>
    </tr>`
      )
      .join('')

    checksSection = `
    <div class="pdf-section">
      <div style="font-weight: 600; font-size: 13px; margin-bottom: 6px; color: #111827;">פירוט שיקים (${checksDetails.length})</div>
      <table width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #d1d5db; margin-bottom: 16px;">
        <tr>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: center; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827; width: 36px;">#</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: right; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">מס׳ שיק</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: right; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">תאריך פרעון</th>
          <th style="background: #f3f4f6; padding: 6px 10px; text-align: left; font-weight: 600; font-size: 11px; border-bottom: 1px solid #9ca3af; color: #111827;">סכום</th>
        </tr>
        ${checkRows}
        <tr>
          <td colspan="3" style="background: #f3f4f6; padding: 8px 10px; text-align: right; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af;">סה״כ שיקים</td>
          <td style="background: #f3f4f6; padding: 8px 10px; text-align: left; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af; direction: ltr; white-space: nowrap;">${formatCurrency(checksAmount)}</td>
        </tr>
      </table>
    </div>`
  }

  // Payment details rows
  let breakdownRows = ''
  if (hasCash) {
    breakdownRows += `
    <tr>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500; font-size: 12px; color: #374151;">מזומן</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 12px; direction: ltr;">${formatCurrency(cashAmount)}</td>
    </tr>`
  }
  if (hasChecks) {
    breakdownRows += `
    <tr style="background: #f9fafb;">
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500; font-size: 12px; color: #374151;">שיקים</td>
      <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 12px; direction: ltr;">${formatCurrency(checksAmount)}</td>
    </tr>`
  }

  // Agent note block
  let noteSection = ''
  if (data.note) {
    noteSection = `
    <div class="pdf-section" style="margin-bottom: 16px; padding: 10px 12px; border: 1px solid #d1d5db; background: #f9fafb;">
      <span style="font-weight: 600; font-size: 12px; color: #111827;">הערת סוכן:</span>
      <span style="font-size: 12px; color: #374151; white-space: pre-wrap;"> ${escapeHtml(data.note)}</span>
    </div>`
  }

  const pdfTitle = data.paymentApprovalNum
    ? `אישור תשלום #${escapeHtml(String(data.paymentApprovalNum))} - ${escapeHtml(data.clientName || '')}`
    : `אישור תשלום - ${escapeHtml(data.clientName || '')}`

  return `
<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pdfTitle}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Rubik', Arial, sans-serif;
      direction: rtl;
      background: #fff;
      color: #111827;
      padding: 20px 24px;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }
    table { border-collapse: collapse; width: 100%; }
    tr { page-break-inside: avoid; break-inside: avoid; }
    table { page-break-inside: avoid; break-inside: avoid; }
    .pdf-section { page-break-inside: avoid; break-inside: avoid; }
  </style>
</head>
<body>
  <div style="max-width: 100%; margin: 0;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 20px;">
      <div style="font-size: 22px; font-weight: 700; color: #111827; margin-bottom: 4px;">GoFood</div>
      <div style="font-size: 15px; font-weight: 600; color: #374151;">אישור תשלום</div>
      <div style="font-size: 10px; color: #6b7280; margin-top: 3px;">${COMPANY_INFO_LINE}</div>
      ${data.paymentApprovalNum ? `<div style="font-size: 13px; color: #374151; margin-top: 4px;">אישור מס׳ ${escapeHtml(String(data.paymentApprovalNum))}</div>` : ''}
    </div>
    ${testNoticeHtml(data, true)}

    <!-- Client Info -->
    <table width="100%" style="margin-bottom: 16px;">
      <tr>
        <td style="vertical-align: top; width: 50%;">
          <div style="font-weight: 600; font-size: 13px; margin-bottom: 4px; border-bottom: 1px solid #d1d5db; padding-bottom: 3px;">פרטי לקוח</div>
          <div style="font-size: 12px; color: #374151; line-height: 1.7;">
            <div><strong>שם:</strong> ${escapeHtml(data.clientName || '—')}</div>
            <div><strong>מס׳ לקוח:</strong> ${escapeHtml(String(data.clientNum || '—'))}</div>
            ${data.clientVat ? `<div><strong>ח.פ:</strong> ${escapeHtml(String(data.clientVat))}</div>` : ''}
          </div>
        </td>
        <td style="vertical-align: top; width: 50%; padding-right: 20px;">
          <div style="font-weight: 600; font-size: 13px; margin-bottom: 4px; border-bottom: 1px solid #d1d5db; padding-bottom: 3px;">פרטי גבייה</div>
          <div style="font-size: 12px; color: #374151; line-height: 1.7;">
            <div><strong>תאריך:</strong> ${dateStr}</div>
            <div><strong>שעה:</strong> ${timeStr}</div>
            <div><strong>סוכן:</strong> ${escapeHtml(data.agentName || '')} (${escapeHtml(String(data.agentNumber || ''))})</div>
          </div>
        </td>
      </tr>
    </table>

    <!-- Payment Summary -->
    <div style="text-align: center; margin-bottom: 20px; padding: 14px; border: 2px solid #111827;">
      <div style="font-size: 12px; font-weight: 500; color: #374151; margin-bottom: 2px;">סכום שהתקבל</div>
      <div style="font-size: 26px; font-weight: 700; color: #111827; direction: ltr; display: inline-block;">
        ${formatCurrency(data.amount)}
      </div>
    </div>

    <!-- Payment Details -->
    <div class="pdf-section">
      <div style="font-weight: 600; font-size: 13px; margin-bottom: 6px; color: #111827;">פרטי התשלום</div>
      <table width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #d1d5db; margin-bottom: 16px;">
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500; font-size: 12px; color: #374151;">אמצעי תשלום</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 12px;">${paymentMethodLabel}</td>
        </tr>
        ${breakdownRows}
        <tr style="background: #f3f4f6;">
          <td style="padding: 8px 10px; text-align: right; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af;">סכום כולל</td>
          <td style="padding: 8px 10px; text-align: left; font-weight: 700; font-size: 13px; border-top: 1px solid #9ca3af; direction: ltr;">${formatCurrency(data.amount)}</td>
        </tr>
      </table>
    </div>

    <!-- Agent Note -->
    ${noteSection}

    <!-- Checks -->
    ${checksSection}

    <!-- Invoices -->
    ${invoicesSection}

    <!-- Footer -->
    <div style="text-align: center; margin-top: 24px; padding-top: 12px; border-top: 1px solid #d1d5db;">
      <div style="font-size: 11px; color: #6b7280;">מסמך זה הופק אוטומטית ממערכת GoFood</div>
      <div style="font-size: 10px; color: #9ca3af; margin-top: 4px;">&copy; ${new Date().getFullYear()} GoFood. כל הזכויות שמורות.</div>
    </div>
  </div>
</body>
</html>
  `.trim()
}
